import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  { letter: "A", text: "La información previa a la acción formativa" },
  { letter: "B", text: "El aula de trabajo" },
  { letter: "C", text: "Los contenidos de las sesiones" },
  { letter: "D", text: "Los medios materiales utilizados" },
  { letter: "E", text: "Las exposiciones del instructor" },
  { letter: "F", text: "La aplicabilidad" },
  { letter: "G", text: "La duración de la acción formativa" },
  { letter: "H", text: "El ambiente grupal" },
  { letter: "I", text: "El intercambio de información" },
  { letter: "J", text: "Valora de manera global, evaluando en su conjunto la acción formativa" },
];

const SCALE = ["Muy deficiente", "Deficiente", "Normal", "Eficiente", "Excelente"];

export default function SatisfactionSurvey({ courseId, student, onSubmitted }) {
  const [answers, setAnswers] = useState({});
  const [comentario, setComentario] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const total = QUESTIONS.length;
  const answered = useMemo(
    () => Object.keys(answers).filter((k) => answers[k]).length,
    [answers]
  );
  const allAnswered = answered === total;

  const setRating = (letter, value) =>
    setAnswers((a) => ({ ...a, [letter]: value }));

  const handleSubmit = async () => {
    if (!allAnswered) {
      toast.error("Valora todas las cuestiones antes de enviar.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/satisfaction-survey`, {
        course_id: courseId,
        dni: student.dni,
        name: student.name,
        surname: student.surname,
        answers,
        comentario,
      });
      setDone(true);
      toast.success("Gracias por tu valoración.");
      setTimeout(() => onSubmitted?.(), 1200);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "No se pudo enviar la encuesta. Inténtalo de nuevo."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 py-10"
        data-testid="survey-done"
      >
        <div className="max-w-xl w-full bg-white border border-zinc-200 rounded-lg p-10 md:p-14 text-center">
          <div className="mx-auto h-20 w-20 flex items-center justify-center rounded-full bg-emerald-50 mb-6">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="heading-font text-4xl font-extrabold text-emerald-700">
            ¡Gracias!
          </h1>
          <p className="mt-3 text-zinc-700">
            Hemos registrado tu valoración. Tus comentarios nos ayudan a
            mejorar.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#FAFAFA]"
      data-testid="satisfaction-survey"
    >
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            Cuestionario de satisfacción
          </div>
          <div className="text-xs font-mono text-zinc-500">
            {answered}/{total}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-8 md:py-12">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
            <span className="inline-block h-2 w-2 bg-black" />
            Encuesta confidencial
          </div>
          <h1 className="heading-font text-4xl sm:text-5xl font-extrabold text-zinc-950 leading-[0.95]">
            ¿Cómo valoras el curso?
          </h1>
          <p className="mt-3 text-zinc-600">
            Tu opinión es importante. Puntúa de 1 (muy deficiente) a 5
            (excelente).
          </p>
        </div>

        <div className="space-y-4">
          {QUESTIONS.map((q) => (
            <div
              key={q.letter}
              className="bg-white border border-zinc-200 rounded-lg p-5"
              data-testid={`survey-q-${q.letter}`}
            >
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 flex items-center justify-center bg-zinc-100 rounded text-sm font-bold text-zinc-700 shrink-0">
                  {q.letter}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-900 font-medium leading-snug">
                    {q.text}
                  </p>
                  <div className="mt-3 grid grid-cols-5 gap-2">
                    {[1, 2, 3, 4, 5].map((n) => {
                      const active = answers[q.letter] === n;
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setRating(q.letter, n)}
                          className={`relative h-12 rounded-md border-2 font-bold text-sm transition-all ${
                            active
                              ? "border-zinc-950 bg-zinc-950 text-white"
                              : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
                          }`}
                          data-testid={`survey-q-${q.letter}-rate-${n}`}
                          aria-label={`${q.text} — ${n} (${SCALE[n - 1]})`}
                        >
                          <span className="absolute top-1 left-2 text-[10px] font-mono opacity-60">
                            {n}
                          </span>
                          <Star
                            className={`mx-auto h-4 w-4 mt-2 ${
                              active ? "fill-white" : ""
                            }`}
                          />
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-1 grid grid-cols-5 gap-2 text-[10px] text-zinc-500 text-center uppercase tracking-wider">
                    {SCALE.map((s) => (
                      <span key={s}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div
            className="bg-white border border-zinc-200 rounded-lg p-5"
            data-testid="survey-comentario-card"
          >
            <label className="block text-sm font-semibold text-zinc-800 mb-2">
              Comentarios (opcional)
            </label>
            <Textarea
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              rows={4}
              placeholder="Aspectos positivos, sugerencias de mejora…"
              className="border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
              data-testid="survey-comentario-input"
              maxLength={1000}
            />
          </div>

          <div className="pt-2 pb-12">
            <Button
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="w-full h-14 bg-zinc-950 hover:bg-zinc-800 text-white text-base font-bold uppercase tracking-wide disabled:opacity-50"
              data-testid="survey-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando…
                </>
              ) : (
                <>Enviar encuesta ({answered}/{total})</>
              )}
            </Button>
            <p className="mt-3 text-center text-xs text-zinc-500">
              Es confidencial. Tus respuestas se usan únicamente para mejorar
              el curso.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
